import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import IconButton from '@mui/material/IconButton';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import Toolbar from '../components/Toolbar';
import AppBar from '../components/AppBar';
import LanguageSelector from '../components/LanguageSelector';
import {
  DEFAULT_LOCALE,
  buildPathWithLocale,
  normalizeLocale,
  setStoredLocale,
} from '../../locales/languages';

const TransparentAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'scrolled',
})(({ theme, scrolled }) => {
  const getHeight = () => (scrolled ? '44px' : '70px');

  const getBackgroundColor = () => (scrolled ? 'transparent' : 'primary.dark');

  const getFontSize = () => (scrolled ? '0.8rem' : '1.2rem');

  return {
    transition: theme.transitions.create(['height', 'background-color'], {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }),
    willChange: 'height, background-color',
    height: getHeight(),
    backgroundColor: getBackgroundColor(),
    boxShadow: scrolled ? theme.shadows[2] : 'none',
    backdropFilter: scrolled ? 'blur(10px)' : 'none',
    fontSize: getFontSize(),
  };
});

function AppAppBar() {
  const [scrolled, setScrolled] = useState(false);

  const handleScroll = () => {
    const nextScrolled = window.scrollY > 80;
    setScrolled((prevScrolled) =>
      prevScrolled === nextScrolled ? prevScrolled : nextScrolled,
    );
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const { i18n } = useTranslation();
  const activeLocale = normalizeLocale(i18n.language) || DEFAULT_LOCALE;
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (newLocale) => {
    setStoredLocale(newLocale);
    i18n.changeLanguage(newLocale);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.pathname = buildPathWithLocale(url.pathname, newLocale);
      window.history.replaceState({}, '', url);
    }
    handleClose();
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div>
      <TransparentAppBar scrolled={scrolled}>
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            mt: scrolled ? -1 : 0,
            color: scrolled ? 'secondary.main' : 'white',
            minHeight: { xs: scrolled ? 44 : 62, sm: scrolled ? 44 : 70 },
            px: { xs: 1.5, sm: 3 },
          }}
        >
          <Box sx={{ flex: 1 }} />
          <Link
            variant="h6"
            underline="none"
            color="inherit"
            href={buildPathWithLocale('/', activeLocale)}
            sx={{
              fontSize: {
                xs: scrolled ? 13 : 15,
                sm: scrolled ? 17 : 24,
              },
              textAlign: 'center',
              lineHeight: 1.1,
              maxWidth: { xs: 220, sm: 'none' },
            }}
          >
            S. LUXURY PRINCESS SUITE
          </Link>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton
              onClick={handleClick}
              sx={{ mt: scrolled ? -1 : 0, color: 'inherit' }}
            >
              <LanguageIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              MenuListProps={{ disablePadding: true }}
              PaperProps={{
                sx: {
                  p: 2,
                  width: 300,
                  maxWidth: '80vw',
                  maxHeight: '70vh',
                  overflowY: 'auto',
                },
              }}
            >
              <LanguageSelector
                currentLocale={i18n.language}
                onChange={handleLanguageChange}
                compact
                showTitle={false}
              />
            </Menu>
          </Box>
        </Toolbar>
      </TransparentAppBar>
      <Toolbar />
    </div>
  );
}

export default AppAppBar;
